import React, { useEffect, useMemo, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import AppModal from "../../Components/Accounting/AppModal";
import Select2 from "../../Components/Accounting/Select2";
import { withPageSecurity } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import ajaxOnboarding from "../../util/accounting/remote/ajaxOnboarding";
import toast, { Toaster } from "react-hot-toast";

const ACCOUNT_KEYS = [
  { key: "default_accounts_receivable", label: "Accounts Receivable (AR)", description: "Used when creating customer invoices and student fee bills", icon: "fa-solid fa-file-invoice-dollar" },
  { key: "default_accounts_payable", label: "Accounts Payable (AP)", description: "Used when creating supplier invoices", icon: "fa-solid fa-money-check-dollar" },
  { key: "default_cash_account", label: "Petty Cash Account", description: "Default cash account for payments", icon: "fa-solid fa-sack-dollar" },
  { key: "default_bank_account", label: "Main Bank Account", description: "Default bank account for transactions", icon: "fa-solid fa-building-columns" },
  { key: "default_sales_revenue", label: "Sales Revenue", description: "Default revenue account for sales", icon: "fa-solid fa-arrow-trend-up" },
  { key: "default_purchase_expense", label: "Purchase Expense", description: "Default expense account for purchases", icon: "fa-solid fa-cart-shopping" },
  { key: "default_retained_earnings", label: "Retained Earnings", description: "Retained earnings equity account", icon: "fa-solid fa-piggy-bank" },
  { key: "default_income_summary", label: "Income Summary", description: "Used when closing a financial year", icon: "fa-solid fa-scale-balanced" },
  { key: "default_tax_payable", label: "Tax Payable (VAT Output)", description: "VAT collected on sales", icon: "fa-solid fa-receipt" },
  { key: "default_tax_receivable", label: "Tax Receivable (VAT Input)", description: "VAT paid on purchases", icon: "fa-solid fa-receipt" },
  { key: "default_discount_given", label: "Discount Given", description: "Sales discount expense account", icon: "fa-solid fa-tag" },
  { key: "default_discount_received", label: "Discount Received", description: "Purchase discount income account", icon: "fa-solid fa-tags" },
  { key: "default_inventory", label: "Inventory", description: "Default inventory/stock asset account", icon: "fa-solid fa-boxes-stacked" },
  { key: "default_cogs", label: "Cost of Goods Sold", description: "Default COGS expense account", icon: "fa-solid fa-truck-ramp-box" },
];

const ORG_KEYS = [
  { key: "org_name", label: "Organisation Name", description: "Appears on reports and invoices", icon: "fa-solid fa-building" },
  // Drives which dashboard, menu and chart-of-accounts seed the system uses.
  // Editable here so it can be corrected without re-running the setup wizard.
  { key: "company_industry", label: "Industry", description: "school | services | trading | manufacturing | ngo | hospitality | healthcare | generic — decides which dashboard and modules you see", icon: "fa-solid fa-industry" },
  { key: "org_address", label: "Organisation Address", description: "Physical address", icon: "fa-solid fa-location-dot" },
  { key: "org_phone", label: "Organisation Phone", description: "Contact phone number", icon: "fa-solid fa-phone" },
  { key: "org_email", label: "Organisation Email", description: "Contact email address", icon: "fa-solid fa-envelope" },
  { key: "org_website", label: "Organisation Website", description: "Shown on printed reports and invoices", icon: "fa-solid fa-globe" },
  { key: "org_tin", label: "TIN / Tax ID", description: "Organisation tax identification number", icon: "fa-solid fa-id-card" },
  { key: "org_logo", label: "Organisation Logo / Badge", description: "Upload the badge printed on every report header. Without one, reports use the organisation's initials.", icon: "fa-solid fa-image" },
  { key: "org_currency", label: "Default Currency", description: "e.g. UGX, USD, KES", icon: "fa-solid fa-coins" },
];

const DOC_KEYS = [
  { key: "invoice_prefix", label: "Invoice Number Prefix", description: "e.g. INV- or SHS-", icon: "fa-solid fa-hashtag" },
  { key: "bill_prefix", label: "Fee Bill Number Prefix", description: "e.g. BILL-", icon: "fa-solid fa-hashtag" },
  { key: "voucher_prefix", label: "Voucher Number Prefix", description: "e.g. V-", icon: "fa-solid fa-hashtag" },
  { key: "invoice_footer", label: "Invoice Footer Note", description: "Shown at the bottom of printed invoices", icon: "fa-solid fa-file-lines" },
  { key: "nssf_employee_rate", label: "NSSF Employee Rate (%)", description: "Employee NSSF contribution percentage", icon: "fa-solid fa-percent" },
  { key: "nssf_employer_rate", label: "NSSF Employer Rate (%)", description: "Employer NSSF contribution percentage", icon: "fa-solid fa-percent" },
];

const formatUpdatedAt = (value) => {
  if (!value) return "Not yet configured";
  const d = new Date(value.replace(" ", "T"));
  if (isNaN(d.getTime())) return "Not yet configured";
  return `Updated: ${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
};

const ConfigCard = ({ cfg, meta, isAccount, accountOptions, onEdit }) => {
  const hasValue = meta?.config_value !== undefined && meta?.config_value !== null && meta?.config_value !== "";
  const displayValue = useMemo(() => {
    if (!hasValue) return null;
    if (isAccount) {
      const opt = accountOptions.find((a) => a.value === String(meta.config_value));
      return opt ? opt.label : meta.config_value;
    }
    return meta.config_value;
  }, [hasValue, isAccount, meta, accountOptions]);

  return (
    <div className="col-lg-4 col-md-6">
      <div className="card custom-card h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-start justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded"
                style={{ width: 36, height: 36, background: "rgba(98,89,202,0.12)" }}
              >
                <i className={`${cfg.icon} tx-primary`} />
              </span>
              <span className="fw-semibold">{cfg.label}</span>
            </div>
            {isAccount && (
              <span className={`badge ${hasValue ? "bg-success-transparent text-success" : "bg-warning-transparent text-warning"}`}>
                {hasValue ? "SET" : "MISSING"}
              </span>
            )}
          </div>
          <div className="tx-11 tx-muted mb-2">{cfg.description}</div>
          <div className="fw-semibold mb-2">
            {!hasValue ? (
              <span className="tx-muted fst-italic">Not set</span>
            ) : cfg.key === LOGO_KEY ? (
              // The badge itself reads better than a long URL.
              <img
                src={meta.config_value}
                alt="Organisation badge"
                style={{ maxHeight: 48, maxWidth: "100%", objectFit: "contain" }}
              />
            ) : (
              displayValue
            )}
          </div>
          <div className="mt-auto d-flex align-items-center justify-content-between pt-2 border-top">
            <span className="tx-11 tx-muted">{formatUpdatedAt(meta?.updated_at)}</span>
            <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(cfg)}>
              <i className="fa-solid fa-pen-to-square me-1" />
              {hasValue ? "Edit" : "Configure"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LOGO_KEY = "org_logo";
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

/**
 * Picks the badge that heads every report. The file is read in the browser and
 * posted as a data URL — the API has no multipart route — and the server
 * re-encodes it, so what comes back is the URL to show, not the file sent.
 */
const LogoField = ({ currentValue, onUploaded, onRemoved, busy, setBusy }) => {
  const [preview, setPreview] = useState(currentValue || "");
  const [error, setError] = useState("");
  const inputRef = React.useRef(null);

  const choose = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked again after an error
    if (!file) return;
    setError("");

    if (!LOGO_TYPES.includes(file.type)) {
      setError("Choose a PNG, JPEG, WebP or GIF image.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError("That image is larger than 2 MB.");
      return;
    }

    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    }).catch(() => null);

    if (!dataUrl) { setError("That file could not be read."); return; }

    setBusy(true);
    const res = await ajaxOnboarding.uploadLogo({ image: dataUrl });
    setBusy(false);

    if (res.status === "OK" && res.details?.url) {
      setPreview(res.details.url);
      onUploaded(res.details.url);
      toast.success("Organisation badge updated");
    } else {
      setError(res.message || "The badge could not be uploaded.");
    }
  };

  const remove = async () => {
    setBusy(true);
    const res = await ajaxOnboarding.removeLogo();
    setBusy(false);
    if (res.status === "OK") {
      setPreview("");
      onRemoved();
      toast.success("Badge removed");
    } else {
      setError(res.message || "The badge could not be removed.");
    }
  };

  return (
    <>
      <label className="form-label tx-12 fw-semibold">Badge image</label>
      <div className="d-flex align-items-center gap-3 mb-2">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded border bg-white"
          style={{ width: 84, height: 84, overflow: "hidden", flexShrink: 0 }}
        >
          {preview ? (
            <img
              src={preview}
              alt="Organisation badge"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <i className="fa-solid fa-image tx-muted" style={{ fontSize: 24 }} />
          )}
        </span>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              <i className="fa-solid fa-upload me-1" />
              {busy ? "Uploading…" : preview ? "Replace image" : "Upload image"}
            </button>
            {preview && (
              <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={remove}>
                <i className="fa-solid fa-trash me-1" />Remove
              </button>
            )}
          </div>
          <span className="tx-11 tx-muted">PNG, JPEG, WebP or GIF — up to 2&nbsp;MB.</span>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={choose}
          style={{ display: "none" }}
          data-testid="logo-file-input"
        />
      </div>

      {error && <div className="tx-12 tx-danger mb-2">{error}</div>}
      <div className="tx-11 tx-muted">
        Printed on every report, invoice and statement. Without one, reports fall
        back to the organisation&rsquo;s initials.
      </div>
    </>
  );
};

const ConfigEditModal = ({ cfg, currentValue, isAccount, accountOptions, onSave, onSaved, onClose }) => {
  const [value, setValue] = useState(currentValue || "");
  const [saving, setSaving] = useState(false);
  const isLogo = cfg.key === LOGO_KEY;

  const handleSubmit = async (e, closeModal) => {
    e.preventDefault();
    setSaving(true);
    const res = await onSave(cfg.key, value);
    setSaving(false);
    if (res.status === "OK") {
      toast.success(res.message || "Saved");
      onSaved(cfg.key, value);
      closeModal();
    } else {
      toast.error(res.message || "Failed to save");
    }
  };

  return (
    <AppModal
      title={`Configure: ${cfg.label}`}
      icon={cfg.icon}
      subtitle={cfg.description}
      onClose={onClose}
      busy={saving}
      onSubmit={(e) => handleSubmit(e, onClose)}
      footer={
        isLogo ? (
          <button type="button" className="btn btn-primary" onClick={onClose} disabled={saving}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-light" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </>
        )
      }
    >
      {isLogo ? (
        // The upload writes the config itself, so there is nothing left to save.
        <LogoField
          currentValue={currentValue}
          busy={saving}
          setBusy={setSaving}
          onUploaded={(url) => { setValue(url); onSaved(cfg.key, url); }}
          onRemoved={() => { setValue(""); onSaved(cfg.key, ""); }}
        />
      ) : (
        <>
          <label className="form-label tx-12 fw-semibold">Value</label>
          {isAccount ? (
            <Select2
              options={accountOptions}
              value={value}
              onChange={(v) => setValue(v)}
              placeholder="Select account"
            />
          ) : (
            <input
              className="form-control"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={cfg.label}
            />
          )}
        </>
      )}
    </AppModal>
  );
};

const ConfigSection = ({ title, subtitle, keys, isAccount, meta, accountOptions, onEdit }) => {
  const [search, setSearch] = useState("");
  const filtered = keys.filter((k) => k.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="card custom-card p-3">
      <p className="tx-13 tx-muted mb-3">{subtitle}</p>
      <div className="mb-3">
        <input
          className="form-control"
          placeholder={`Search ${title.toLowerCase()}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="row g-3">
        {filtered.map((cfg) => (
          <ConfigCard
            key={cfg.key}
            cfg={cfg}
            meta={meta[cfg.key]}
            isAccount={isAccount}
            accountOptions={accountOptions}
            onEdit={onEdit}
          />
        ))}
        {filtered.length === 0 && <div className="col-12 tx-muted">No matching settings.</div>}
      </div>
    </div>
  );
};

const TABS = [
  { key: "accounts", label: "Special Accounts", icon: "fa-building-columns" },
  { key: "org", label: "Organisation Details", icon: "fa-sliders" },
  { key: "docs", label: "Billing & Documents", icon: "fa-file-invoice" },
];

const SystemConfig = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountMeta, setAccountMeta] = useState({});
  const [orgMeta, setOrgMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { cfg, isAccount }
  const [tab, setTab] = useState("accounts");

  useEffect(() => {
    fetchAccounts();
    fetchAll();
  }, []);

  const fetchAccounts = async () => {
    const res = await ajaxAccounting.listAccounts({ postable_only: 1 });
    if (res.status === "OK") setAccounts(res.details || []);
  };

  const fetchAll = async () => {
    setLoading(true);
    const [sysRes, orgRes] = await Promise.all([
      ajaxAccounting.listSystemConfig(),
      ajaxAccounting.listConfig(),
    ]);
    if (sysRes.status === "OK") {
      const meta = {};
      (sysRes.details || []).forEach((c) => { meta[c.config_key] = { config_value: c.config_value, updated_at: c.updated_at }; });
      setAccountMeta(meta);
    }
    if (orgRes.status === "OK") {
      const meta = {};
      (orgRes.details || []).forEach((c) => { meta[c.config_key] = { config_value: c.config_value, updated_at: c.updated_at }; });
      setOrgMeta(meta);
    }
    setLoading(false);
  };

  const accountOptions = accounts.map((a) => ({ value: String(a.id), label: `${a.account_code} — ${a.name}` }));

  const handleAccountSaved = (key, value) => {
    setAccountMeta((m) => ({ ...m, [key]: { ...(m[key] || {}), config_value: value, updated_at: new Date().toISOString() } }));
  };
  const handleOrgSaved = (key, value) => {
    setOrgMeta((m) => ({ ...m, [key]: { ...(m[key] || {}), config_value: value, updated_at: new Date().toISOString() } }));
  };

  const editingCfg = editing
    ? [...ACCOUNT_KEYS, ...ORG_KEYS, ...DOC_KEYS].find((c) => c.key === editing.key)
    : null;

  return (
    <AppContainer
      title="System Configuration"
      previousPageTitle="Accounting"
      description="Configure system-wide defaults. Account mappings tell Accountify which GL accounts to use when auto-posting invoices, fee bills, payments, taxes, and discounts."
      actions={[
        { label: "Refresh", icon: `fa-solid fa-arrows-rotate ${loading ? "fa-spin" : ""}`, className: "btn btn-outline-secondary", onClick: fetchAll },
      ]}
    >
      <Toaster />

      <ul className="nav nav-tabs mb-3">
        {TABS.map((t) => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <i className={`fa-solid ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === "accounts" && (
        <ConfigSection
          title="Special Accounts"
          subtitle="Link system functions to chart of accounts"
          keys={ACCOUNT_KEYS}
          isAccount
          meta={accountMeta}
          accountOptions={accountOptions}
          onEdit={(cfg) => setEditing({ key: cfg.key, isAccount: true })}
        />
      )}

      {tab === "org" && (
        <ConfigSection
          title="Organisation Details"
          subtitle="Used on reports, statements and invoices"
          keys={ORG_KEYS}
          isAccount={false}
          meta={orgMeta}
          accountOptions={accountOptions}
          onEdit={(cfg) => setEditing({ key: cfg.key, isAccount: false })}
        />
      )}

      {tab === "docs" && (
        <ConfigSection
          title="Billing & Documents"
          subtitle="Number prefixes, footers and payroll statutory rates"
          keys={DOC_KEYS}
          isAccount={false}
          meta={orgMeta}
          accountOptions={accountOptions}
          onEdit={(cfg) => setEditing({ key: cfg.key, isAccount: false })}
        />
      )}

      {editingCfg && (
        <ConfigEditModal
          key={editingCfg.key}
          cfg={editingCfg}
          currentValue={editing.isAccount ? accountMeta[editingCfg.key]?.config_value : orgMeta[editingCfg.key]?.config_value}
          isAccount={editing.isAccount}
          accountOptions={accountOptions}
          onSave={(key, value) =>
            editing.isAccount
              ? ajaxAccounting.setSystemConfig({ config_key: key, config_value: value })
              : ajaxAccounting.setConfig({ config_key: key, config_value: value })
          }
          onSaved={editing.isAccount ? handleAccountSaved : handleOrgSaved}
          onClose={() => setEditing(null)}
        />
      )}
    </AppContainer>
  );
};

// VIEW-ACCOUNTS, not the "ACC-CONFIG" this page arrived with. Those ACC-* codes
// belong to the accounting module's own per-role catalogue, which this system's
// permissions know nothing about — so nobody held the code, the wrapper rendered
// nothing, and the page came up blank for every user including root. Accounting
// here is gated once, on the same code that puts it in the side menu.
export default withPageSecurity(SystemConfig, "VIEW-ACCOUNTS");
